/**
 * NexusEngine — Core game loop
 *
 * Responsibilities:
 * - requestAnimationFrame loop with dt-capped delta time
 * - System pipeline (update → render)
 * - Hit-stop freeze frame support
 * - UI bridge to React (throttled)
 * - Pause/resume, game state
 *
 * This runs OUTSIDE React's lifecycle. React only wraps the canvas
 * and renders HUD overlays via the uiBridge callback.
 */

import { EntityStore } from './EntityStore';
import { JuicePack } from './JuicePack';
import { AudioManager } from './AudioManager';
import { InputAdapter } from './InputAdapter';

export class Engine {
  constructor(canvas, config = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.config = config;

    // Core subsystems
    this.entities = new EntityStore();
    this.juice = new JuicePack();
    this.audio = new AudioManager();
    this.input = new InputAdapter(canvas);

    // System pipeline (order matters: movement → collision → spawn → weapon → pickup → render)
    this.systems = [];
    this.renderSystem = null; // special: runs in draw phase, not update

    // Loop control
    this.running = false;
    this.paused = false;
    this.lastTime = 0;
    this.elapsed = 0; // total game time in seconds
    this.frameCount = 0;

    // Game state (engine-level, not game-specific)
    this.state = {
      score: 0,
      kills: 0,
      combo: 0,
      comboTimer: 0,
      comboDecay: 2.0, // seconds before combo resets
      level: 1,
      xp: 0,
      xpToNext: 100,
      hp: 100,
      maxHp: 100,
      wave: 1,
      gameOver: false,
      levelUpPending: false,
    };

    // Player stats (modified by upgrades)
    this.playerStats = {
      moveSpeed: 200,
      fireRate: 8,          // shots per second
      damage: 1,
      projectileCount: 3,
      projectileSpread: 15, // degrees
      projectileSpeed: 400,
      pickupRadius: 40,
      critChance: 0,
      critMultiplier: 1.5,
      shieldHP: 0,
      chainTargets: 0,
      chainDamageDecay: 0,
      healOnKill: 0,
    };

    // Upgrade history
    this.upgrades = [];

    // UI bridge — React sets this to get HUD updates
    this.uiBridge = null;
    this._lastUIUpdate = 0;
    this._uiUpdateInterval = 100; // ms — throttle React updates to ~10fps

    // Callbacks
    this.onGameOver = null;
    this.onLevelUp = null;

    // Resize handler
    this._resizeCanvas();
    this._resizeHandler = () => this._resizeCanvas();
    window.addEventListener('resize', this._resizeHandler);
  }

  // --- Public API ---

  addSystem(system) {
    this.systems.push(system);
    return this;
  }

  setRenderSystem(system) {
    this.renderSystem = system;
    return this;
  }

  setUIBridge(fn) {
    this.uiBridge = fn;
  }

  start() {
    this.running = true;
    this.paused = false;
    this.lastTime = performance.now();
    this._loop(this.lastTime);
  }

  stop() {
    this.running = false;
    this.input.destroy();
    window.removeEventListener('resize', this._resizeHandler);
  }

  pause() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
    this.lastTime = performance.now(); // reset dt to avoid spike
  }

  // --- Game Logic Helpers ---

  addScore(points) {
    const comboMultiplier = 1 + (this.state.combo * 0.1); // 10% per combo
    this.state.score += Math.round(points * comboMultiplier);
    this.state.combo++;
    this.state.comboTimer = this.state.comboDecay;
  }

  addKill() {
    this.state.kills++;
    if (this.playerStats.healOnKill > 0) {
      this.state.hp = Math.min(this.state.maxHp, this.state.hp + this.playerStats.healOnKill);
    }
  }

  addXP(amount) {
    this.state.xp += amount;
    while (this.state.xp >= this.state.xpToNext) {
      this.state.xp -= this.state.xpToNext;
      this.state.level++;
      this.state.xpToNext = Math.round(100 * Math.pow(this.state.level, 1.5));
      this.state.levelUpPending = true;
      if (this.onLevelUp) this.onLevelUp(this.state.level);
    }
  }

  takeDamage(amount) {
    // Shield absorbs first
    if (this.playerStats.shieldHP > 0) {
      this.playerStats.shieldHP -= amount;
      if (this.playerStats.shieldHP < 0) {
        this.state.hp += this.playerStats.shieldHP; // overflow damage
        this.playerStats.shieldHP = 0;
      }
    } else {
      this.state.hp -= amount;
    }

    this.juice.shake(6, 0.15);

    if (this.state.hp <= 0) {
      this.state.hp = 0;
      this.state.gameOver = true;
      if (this.onGameOver) {
        this.onGameOver({
          score: this.state.score,
          kills: this.state.kills,
          level: this.state.level,
          timeSurvived: this.elapsed,
          wave: this.state.wave,
        });
      }
    }
  }

  applyUpgrade(upgrade) {
    this.upgrades.push(upgrade.id);
    const e = upgrade.effect;
    if (e.fireRateMultiplier) this.playerStats.fireRate *= e.fireRateMultiplier;
    if (e.damageMultiplier) this.playerStats.damage *= e.damageMultiplier;
    if (e.moveSpeedMultiplier) this.playerStats.moveSpeed *= e.moveSpeedMultiplier;
    if (e.pickupRadius) this.playerStats.pickupRadius *= e.pickupRadius;
    if (e.projectileCount) this.playerStats.projectileCount += e.projectileCount;
    if (e.shieldHP) this.playerStats.shieldHP += e.shieldHP;
    if (e.critChance) this.playerStats.critChance += e.critChance;
    if (e.critMultiplier) this.playerStats.critMultiplier = e.critMultiplier;
    if (e.chainTargets) this.playerStats.chainTargets += e.chainTargets;
    if (e.chainDamageDecay) this.playerStats.chainDamageDecay = e.chainDamageDecay;
    if (e.healOnKill) this.playerStats.healOnKill += e.healOnKill;
  }

  // --- Core Loop ---

  _loop(now) {
    if (!this.running) return;
    requestAnimationFrame(t => this._loop(t));

    if (this.paused || this.state.gameOver) {
      // Still render (for pause screen overlays) but don't update
      this._draw();
      return;
    }

    const dt = Math.min((now - this.lastTime) / 1000, 0.1); // cap at 100ms
    this.lastTime = now;
    this.elapsed += dt;
    this.frameCount++;

    // Combo decay
    if (this.state.comboTimer > 0) {
      this.state.comboTimer -= dt;
      if (this.state.comboTimer <= 0) {
        this.state.combo = 0;
      }
    }

    // Hit-stop: skip update but keep drawing
    if (this.juice.isFrozen()) {
      this.juice.tickFreeze();
      this._draw();
      this._updateUI(now);
      return;
    }

    // Update all systems
    for (const sys of this.systems) {
      sys.update(dt, this.entities, this);
    }

    // Update juice (particles, shake decay, etc.)
    this.juice.update(dt);

    // Draw
    this._draw();

    // Throttled UI bridge
    this._updateUI(now);
  }

  _draw() {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;

    // Trails effect: semi-transparent fill instead of full clear
    ctx.fillStyle = 'rgba(6, 6, 12, 0.15)';
    ctx.fillRect(0, 0, w, h);

    // Apply screen shake
    ctx.save();
    this.juice.applyShake(ctx);

    // Render all entities via render system
    if (this.renderSystem) {
      this.renderSystem.draw(ctx, this.entities, this);
    }

    // Draw particles on top
    this.juice.drawParticles(ctx);

    ctx.restore();
  }

  _updateUI(now) {
    if (!this.uiBridge) return;
    if (now - this._lastUIUpdate < this._uiUpdateInterval) return;
    this._lastUIUpdate = now;

    this.uiBridge({
      score: this.state.score,
      kills: this.state.kills,
      combo: this.state.combo,
      level: this.state.level,
      xp: this.state.xp,
      xpToNext: this.state.xpToNext,
      hp: this.state.hp,
      maxHp: this.state.maxHp,
      wave: this.state.wave,
      gameOver: this.state.gameOver,
      levelUpPending: this.state.levelUpPending,
      elapsed: this.elapsed,
    });
  }

  _resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this.ctx.scale(dpr, dpr);
    this.width = w;
    this.height = h;
  }
}
